import { country, CountryCode } from "../../../shared/models/countryModels";
import { CustomerSizeCode, CustomerTurnoverCode, DayOfWeek, FileModel, PaymentMethodCode, ShippingOptionCode } from "../../../shared/models/models";
import { CustomerContact } from "./customerContactsModels";

export interface Register {
  userName: string;
  roleCode: RoleCode;
  /** EMAIL-LANG-WIRING: UI locale for the confirmation email (en/sk/cs); server-normalized, unknown -> EN. */
  language?: string;
}

export interface Registration {
  userId: string;
  userName: string;
  roleCode: RoleCode;
}

export interface ConfirmRegister {
  userId: string;
  firstName: string;
  lastName: string;
  password: string;
  token: string;
  name: string;
  customIndustryName: string;
  countryId: number;
  customerType: number;
  partnerInviteToken?: string;
  /** EMAIL-LANG-WIRING: 1=en, 3=cs, 4=sk - initial profile language + welcome-email language. */
  languageId?: number;
}

export enum CustomerTypeCode {
  MachineOperator = 1,
  ServiceProvider = 2,
  Both = 3,
}

export interface ConfirmRegisterResult {
  userName: string;
  isPendingApproval?: boolean;
}

export interface ConfirmInvite {
  userId: string;
  firstName: string;
  lastName: string;
  password: string;
  token: string;
}

export interface ConfirmInviteForm {
  userId: string;
  userName: string;
  companyName: string;
  firstName?: string;
  lastName?: string;
}

export interface ConfirmInviteResult {
  userName: string;
}

export interface Customer {
  id: string;
  name: string;
  roleCode: RoleCode;
  description: string;
  logo: FileModel | null;
  banner: FileModel | null;
  website: string;
  establishingYear?: number;
  vatId: string;
  taxId: string;
  registrationNumber: string;
  turnoverCode: CustomerTurnoverCode;
  sizeCode: CustomerSizeCode;
  customerType?: number;
  customerIndustry: CustomerIndustry;
  manufacturers: ManufacturerReference[];
  categories: CategoryReference[];
  paymentMethodCodes: PaymentMethodCode[];
  shippingOptionCodes: ShippingOptionCode[];
  defaultContact?: CustomerContact;
  addresses: Address[];
  warehouse?: Warehouse;
  isDisabled: boolean;
  createdDate: Date;
  modifiedDate: Date;
  linkedInLink: string;
  instagramLink: string;
  xLink: string;
  facebookLink: string;
}

export interface Warehouse {
  address: string;
  opensAt: string;
  closesAt: string;
  daysOfWeek: DayOfWeek[];
}

export interface WarehouseWorkingDay {
  warehouseId: string;
  daysOfWeek: DayOfWeek[];
  opensAt: Date;
  closesAt: Date;
}

export interface Address {
  typeCode: AddressType;
  address?: string;
  city?: string;
  countryId: CountryCode;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
}

export const formatAddressString = (address?: Address): string | undefined => {
  const addressLine = address?.address?.trim();
  const city = address?.city?.trim();

  if (!addressLine && !city) {
    return undefined;
  }

  const countryName = address?.countryId ? country(address.countryId)?.name : undefined;
  const addressString = [addressLine, city, countryName].filter(x => x?.trim()).join(", ");

  return addressString;
};

export interface AddressType {
  id: AddressTypeCode;
  name: string;
}

export enum AddressTypeCode {
  billing = 1,
  shipping = 2,
  legal = 3
}

export interface ManufacturerReference {
  id: string;
  name: string;
}

export interface CategoryReference {
  id: string;
  name: string;
}

export interface CustomerIndustry {
  customerId: string;
  industry: IndustryCode;
}

export enum IndustryCode {
  industry001 = 1,
  industry002 = 2
}

export enum RoleCode {
  buyer = 1,
  supplier = 2
}

class Role {
  private readonly map = {} as any;

  constructor() {
    this.map[RoleCode.buyer] = "Buyer";
    this.map[RoleCode.supplier] = "Supplier";
  }

  roleCode = (code: RoleCode) => {
    return this.map[code] || '';
  };
}
export const { roleCode } = new Role();

export interface MultipleSelectFilterParameter {
  id: number | string;
  isEnabled: boolean;
  name: string;
  value: number;
}

export interface AccountUserInfo {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  userName: string;
  phoneNumber?: string;
  department?: string;
  fontTheme?: string;
  accentTheme?: string;
  languageId?: number;
}

export interface UpdateAccount {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  department?: string;
  fontTheme?: string;
}

export interface ChangePassword {
  oldPassword: string;
  newPassword: string;
}

export interface ChangeAccountPasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
} 